import { AuthenticationManager } from "@playit/shared-authentication";
import axios from "axios";
import { OutfitDTO, OutfitRenderDTO } from "../dto/OutfitDTO";
import { Outfit } from "../models/Outfit";

export class OutfitService {
  private static readonly baseUrl = `${process.env.REACT_APP_PLAYIT_CREATORAPI}`;

  // the API caps name-paginated lists at 10 per page
  static readonly pageSize = 10;

  private static getAuthConfig() {
    const token = AuthenticationManager.GetToken();

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  private static getTotalCount(headers: Record<string, unknown> | undefined) {
    const paginationHeader = headers?.["x-pagination"];
    if (typeof paginationHeader !== "string") {
      throw new Error("Missing x-pagination header");
    }

    return JSON.parse(paginationHeader)["totalCount"] as number;
  }

  private static getStatus(error: unknown) {
    return (error as { response?: { status?: number } })?.response?.status;
  }

  private static outfitUrl(orgId: string, name: string) {
    return `${this.baseUrl}/organizations/${orgId}/outfits/${encodeURIComponent(name)}`;
  }

  private static toOutfit(name: string, render?: OutfitRenderDTO): Outfit {
    return {
      name,
      malePreviewUri: render?.maleDownloadUri ?? undefined,
      femalePreviewUri: render?.femaleDownloadUri ?? undefined,
    };
  }

  /** One page of outfits (1-based) with their previews, plus the catalog total. */
  static async GetOutfitsPage(
    orgId: string,
    page: number,
    name?: string
  ): Promise<{ outfits: Outfit[]; totalCount: number } | undefined> {
    const path = `${this.baseUrl}/organizations/${orgId}/outfits`;
    const config = {
      ...this.getAuthConfig(),
      params: {
        PageSize: this.pageSize,
        PageNumber: page,
        // the API matches names containing this, case-insensitively
        ...(name ? { Name: name } : {}),
      },
    };

    try {
      const { data, headers } = await axios.get<OutfitDTO[]>(path, config);
      const names = (data ?? []).map((outfit) => outfit.name);
      const renders = await Promise.all(
        names.map((name) => this.GetOutfitRender(orgId, name))
      );

      return {
        outfits: names.map((name, index) => this.toOutfit(name, renders[index])),
        totalCount: this.getTotalCount(headers),
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  /** The male + female render of an outfit; undefined when it has none (404). */
  static async GetOutfitRender(
    orgId: string,
    name: string
  ): Promise<OutfitRenderDTO | undefined> {
    try {
      const { data } = await axios.get<OutfitRenderDTO>(
        `${this.outfitUrl(orgId, name)}/render`,
        this.getAuthConfig()
      );
      return data;
    } catch (error) {
      // 404 means no render was uploaded yet (or no such outfit); not an error
      if (this.getStatus(error) !== 404) {
        console.error(error);
      }
      return undefined;
    }
  }

  /** Whether an outfit with this name exists. */
  static async OutfitExists(orgId: string, name: string): Promise<boolean> {
    try {
      await axios.get<OutfitDTO>(this.outfitUrl(orgId, name), this.getAuthConfig());
      return true;
    } catch (error) {
      if (this.getStatus(error) !== 404) {
        console.error(error);
      }
      return false;
    }
  }

  /**
   * The named outfits that exist, with their previews: one render request per
   * name, plus an existence check only for names without a render.
   */
  static async GetOutfitsByName(orgId: string, names: string[]): Promise<Outfit[]> {
    const unique = Array.from(new Set(names.filter((name) => name)));
    const outfits = await Promise.all(
      unique.map(async (name) => {
        const render = await this.GetOutfitRender(orgId, name);
        if (render) return this.toOutfit(name, render);
        return (await this.OutfitExists(orgId, name)) ? this.toOutfit(name) : undefined;
      })
    );

    return outfits.filter((outfit): outfit is Outfit => outfit !== undefined);
  }
}
